import React from 'react';
import ReactGA from 'react-ga';
import campusDiveHero from '../../../assets/images/campusDiveHero.gif';
import takeawaysEmoji from '../../../assets/images/conditionsCaseStudy/takeaways-emoji.svg';
import {
  CaseStudyBulletList,
  CaseStudyContentCard,
  CaseStudyDoubleCardRow,
  CaseStudyHero,
  CaseStudyOverviewColumn,
  CaseStudyOverviewGrid,
  CaseStudySecondaryNav,
  CaseStudySectionHeader,
  CaseStudySplitSection,
  CaseStudyStatsBar,
} from '../../../components/cards/caseStudyCards/caseStudyCards';
import CdDesignSection, { FindingBeforeAfterArrowMirrored } from './cdDesignSection';
import ds from './cdDesignSection.module.scss';
import ScrollToTopButton from '../../../components/scrollToTopButton/ScrollToTopButton';
// Placeholder hybrid tension before/after assets — replace paths when final art is ready.
import tensionBeforeAfterBefore from '../../../assets/images/cd/4_tensionpoint_before.png';
import tensionBeforeAfterAfter from '../../../assets/images/cd/4_tensionpoint_after.gif';
import cards from '../../../components/cards/caseStudyCards/caseStudyCards.module.scss';
import page from './cd.module.scss';

const CAMPUS_DIVE_URL = 'https://campusdive.com/';
// Large SVG from design tools breaks @svgr/webpack; serve from /public so it loads as a plain URL.
const CD_CONTEXT_INFOGRAPHIC_SRC = `${process.env.PUBLIC_URL || ''}/assets/cd/1_Context_CD.svg`;

const NAV_ITEMS = [
  { id: 'section-intro', label: 'Intro' },
  { id: 'section-context', label: 'Context' },
  { id: 'section-problem', label: 'Problem Definition' },
  { id: 'section-design', label: 'Design Based On Findings' },
  { id: 'section-tension-point', label: 'Tension Point' },
  { id: 'section-outcome', label: 'Outcome' },
  { id: 'section-takeaways', label: 'Takeaways' },
];

const CD_HERO_TAGS = [
  'Product Design',
  'UX Design',
  'UI Design',
  'Data Visualization',
  'Feature Development',
  'Edtech',
  'B2C',
  'Startup',
  'Branding',
];

export default function CdCaseStudy() {
  const [activeSection, setActiveSection] = React.useState('section-intro');
  const scrollLockRef = React.useRef(null);
  const scrollLockTimerRef = React.useRef(null);
  const navWrapRef = React.useRef(null);

  ReactGA.pageview(window.location.pathname + window.location.search);

  React.useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const updateActiveFromScroll = React.useCallback(() => {
    if (scrollLockRef.current) return;

    const scrollSpyOffset = (navWrapRef.current?.offsetHeight ?? 72) + 48;
    const scrollPos = window.scrollY + scrollSpyOffset;
    let current = NAV_ITEMS[0].id;

    for (const { id } of NAV_ITEMS) {
      const el = document.getElementById(id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top <= scrollPos) {
        current = id;
      }
    }

    setActiveSection(current);
  }, []);

  React.useEffect(() => {
    updateActiveFromScroll();

    const onScroll = () => {
      updateActiveFromScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateActiveFromScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateActiveFromScroll);
      if (scrollLockTimerRef.current) {
        window.clearTimeout(scrollLockTimerRef.current);
      }
    };
  }, [updateActiveFromScroll]);

  const scrollToSection = (id) => {
    if (scrollLockTimerRef.current) {
      window.clearTimeout(scrollLockTimerRef.current);
    }

    scrollLockRef.current = id;
    setActiveSection(id);

    const el = document.getElementById(id);
    if (el) {
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const navH = navWrapRef.current?.offsetHeight ?? 72;
      const targetTop = id === 'section-intro' ? docTop : docTop - navH;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }

    scrollLockTimerRef.current = window.setTimeout(() => {
      scrollLockRef.current = null;
      scrollLockTimerRef.current = null;
      updateActiveFromScroll();
    }, 900);
  };

  return (
    <main className={page.page}>
      <div id="section-intro">
        <div className={`${page.inner} ${page.heroBand}`}>
          <CaseStudyHero
            title="Closing the College Guidance Gap Through Better Product Design"
            tags={CD_HERO_TAGS}
            subhead="Product re-design for scale ahead of new feature development"
            ctaHref={CAMPUS_DIVE_URL}
            ctaLabel="Visit Campus Dive"
            heroImageSrc={campusDiveHero}
            heroImageAlt="Campus Dive laptop and phone mockups showing academic profile and sign-in experience"
            heroImageAspectRatio="1820 / 831"
          />
        </div>

        <div className={`${page.inner} ${page.overviewBand}`}>
          <CaseStudyOverviewGrid>
            <CaseStudyOverviewColumn label="Timeline">
              <ul className={cards.overviewList}>
                <li>1 Quarter</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="My role">
              <ul className={cards.overviewList}>
                <li>Product Designer</li>
                <li>UX Researcher</li>
                <li>Brand Designer</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Team">
              <ul className={cards.overviewList}>
                <li>4 UX Designer/Researchers</li>
                <li>Product Manager</li>
                <li>2 Engineers</li>
              </ul>
            </CaseStudyOverviewColumn>
            <CaseStudyOverviewColumn label="Scope" wide>
              <ul className={cards.overviewList}>
                <li>
                  Conduct research including product audit, comparative analysis, heuristic evaluation and
                  market research to inform product direction.
                </li>
                <li>New feature development for Student Survey and Interview to address core user goals.</li>
                <li>
                  Visual Design to define brand voice and create a scalable unified visual language.
                </li>
              </ul>
            </CaseStudyOverviewColumn>
          </CaseStudyOverviewGrid>
        </div>
      </div>

      <div ref={navWrapRef} className={page.navWrap}>
        <CaseStudySecondaryNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />
      </div>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-context">
        <div className={page.inner}>
          <CaseStudySplitSection
            kicker="CONTEXT"
            title="What Was at Risk"
            imageSrc={CD_CONTEXT_INFOGRAPHIC_SRC}
            imageAlt="Infographic overview titled Applying to College in 2023, with U.S. college counts and enrollment statistics"
            figureClassName={cards.splitFigureTall}
            captionLabel="Overview of 2023 College Application Statistics"
            captionCenter
            largeFooterGap
            splitFooter={
              <p className={cards.splitFooterQuestion}>
                If we scaled the existing experience, we would scale confusion.
              </p>
            }
          >
            <div className={cards.splitBodyColumn}>
              <p>
                For many students, choosing a college is one of the first high-stakes decisions they make
                &mdash; and one of the least supported.
              </p>
              <p className={cards.splitBodyParagraphGap}>
                There are nearly 4,000 colleges in the U.S., and access to guidance is uneven. Campus Dive
                aimed to close that gap by providing anonymous, first-hand student interviews to help high
                schoolers make informed decisions.
              </p>
              <p>But the product had a fundamental problem:</p>
              <ul className={cards.splitBodyListTight}>
                <li>Students couldn&apos;t find what they needed</li>
                <li>Content was dense and difficult to scan</li>
                <li>Navigation relied almost entirely on search</li>
                <li>The experience created more overwhelm than clarity</li>
              </ul>
              <p>At this stage, the company was preparing to expand its product suite.</p>
            </div>
          </CaseStudySplitSection>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-problem">
        <div className={page.inner}>
          <div className={page.problemSection}>
            <CaseStudySectionHeader
              kicker="PROBLEM DEFINITION"
              title="Strategic Fork: Internal Misalignment was Slowing the Product"
            />
            <div className={page.problemRow}>
              <div className={page.problemLeft}>
                <div className={page.problemItem}>
                  <p className={page.problemLabel}>CONTEXT</p>
                  <p>
                    Part of the first formal UX team, the product was built without design/product
                    oversight. The initial ask was to &ldquo;Improve the UI.&rdquo;
                  </p>
                </div>
                <div className={page.problemItem}>
                  <p className={page.problemLabel}>SCOPE</p>
                  <p>
                    It quickly became clear that this was not a UI problem. The product faced a
                    signal-to-noise challenge across the system, including:
                  </p>
                  <ul>
                    <li>Content strategy gaps</li>
                    <li>A confusing navigation model</li>
                    <li>Inconsistent data structure</li>
                    <li>Weak brand clarity</li>
                    <li>Misaligned product roadmap priorities</li>
                  </ul>
                </div>
                <div className={page.problemItem}>
                  <p className={page.problemLabel}>CHALLENGE</p>
                  <p>
                    The team disagreed on what to prioritize. Some advocated for building new features,
                    while I identified foundational issues that needed immediate attention: navigation,
                    wayfinding, and brand cohesion. This wasn&apos;t a minor disagreement &mdash; it was
                    a strategic fork.
                  </p>
                </div>
              </div>
              <div className={page.problemDecisionWrap}>
                <CaseStudyContentCard label="Design decision" labelTone="teal" surface="none">
                  <div className={page.problemDecisionCardBody}>
                    <p className={cards.cardIntro}>
                      <strong>Prioritize foundation over expansion.</strong>
                    </p>
                    <p className={cards.cardIntro}>
                      Adding features on top of a broken foundation risked increasing complexity without
                      delivering value.
                    </p>
                    <div className={cards.cardFocusBlock}>
                      <p className={cards.cardIntro}>I made the case to focus on:</p>
                      <ul className={cards.cardList}>
                        <li>Navigation redesign to improve wayfinding</li>
                        <li>Search and filter system to enhance content discoverability</li>
                        <li>Brand identity and UI cohesion to create a consistent experience</li>
                      </ul>
                    </div>
                    <p className={cards.cardIntro}>
                      After presenting a cross-functional rationale grounded in user research and business
                      impact, the team aligned on this direction, setting the stage for more effective
                      feature development.
                    </p>
                  </div>
                </CaseStudyContentCard>
              </div>
            </div>
            <p className={page.problemStatement}>
              This approach aligned both user needs and business goals.
            </p>
          </div>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain}`} id="section-design">
        <div className={page.inner}>
          <CdDesignSection />
        </div>
      </section>

      <section className={`${page.section} ${page.sectionPorcelain}`} id="section-tension-point">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader kicker="TENSION POINT" title="Compromise between quantifiable & quantitative data" />
            <CaseStudyDoubleCardRow
              className={`${ds.doubleCardRowHybridLayout} ${ds.doubleCardRowNormalLineHeight}`}
              left={
                <CaseStudyContentCard surface="porcelain" className={cards.contentCardPaddingNone}>
                  <div className={ds.cardCopyStack}>
                    <p className={ds.cardCopy}>
                      The team proposed converting interview content into fully quantifiable formats. I pushed back.
                    </p>
                    <p className={ds.cardCopy}>
                      <strong>
                        {`Over-structuring risked diluting Campus Dive's core value: authentic, first-hand student experiences. Turning rich narratives into data points would make the product more comparable\u2014but less differentiated.`}
                      </strong>
                    </p>
                    <div className={ds.cardCopyStackLeadIn}>
                      <p className={ds.cardCopy}>Decision: Adopt a hybrid model</p>
                      <ul className={`${cards.cardList} ${ds.cardListTight}`}>
                        <li>
                          Preserved qualitative storytelling to maintain authenticity and emotional resonance
                        </li>
                        <li>
                          Introduced structured data to support navigation, filtering, and comparison
                        </li>
                        <li>
                          Refined survey design to intentionally capture both narrative depth and measurable
                          signals
                        </li>
                      </ul>
                    </div>
                  </div>
                </CaseStudyContentCard>
              }
              right={
                <div className={ds.callout}>
                  <p className={ds.calloutLabel}>IMPACT</p>
                  <p className={ds.calloutTitle}>
                  Balanced depth with usability; enabling scalable discovery without sacrificing the product’s unique value proposition.
                  </p>
                </div>
              }
            />
            <div
              className={`${ds.beforeAfter} ${ds.beforeAfterUnevenMocks} ${ds.beforeAfterFinding1Laptop}`}
            >
              <div className={ds.beforeAfterBottom}>
                <div className={`${ds.beforeAfterCol} ${ds.beforeAfterColAfter}`}>
                  <p className={`${ds.mockLabel}`}>AFTER</p>
                  <div className={`${ds.mockAfter} ${ds.mockAfterWide}`}>
                    <img
                      src={tensionBeforeAfterAfter}
                      alt="After state (placeholder — update when final asset is ready)"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={ds.beforeAfterFlowArrow}>
                  <FindingBeforeAfterArrowMirrored />
                </div>
                <div className={`${ds.beforeAfterCol} ${ds.beforeAfterColBefore}`}>
                  <p className={ds.mockLabel}>BEFORE</p>
                  <div className={ds.tensionBeforeAfterBeforeWrap}>
                    <img
                      src={tensionBeforeAfterBefore}
                      alt="Campus Dive tension point: before — qualitative vs quantitative framing"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`${page.section} ${page.sectionDarkPorcelain} ${page.sectionOutcomeTight}`} id="section-outcome">
        <div className={page.inner}>
          <div className={cards.sectionBlock}>
            <CaseStudySectionHeader kicker="OUTCOME" title="Turning Complexity into Clarity at Scale" />
            <div className={page.outcomeWithStatsRow}>
              <div className={page.outcomeImpactStack}>
                <div className={page.outcomeImpactBlock}>
                  <p className={page.outcomeImpactLabel}>Product impact</p>
                  <ul className={page.outcomeImpactList}>
                    <li>
                      Reduced search and filter response time by 50%, improving system reliability and trust
                    </li>
                    <li>
                      Decreased student intake time by 75%, enabling faster onboarding and engagement
                    </li>
                    <li>Transformed dense, long-form interviews into layered, navigable content</li>
                    <li>Established a scalable design system, creating consistency across the product</li>
                  </ul>
                </div>
                <div className={page.outcomeImpactBlock}>
                  <p className={page.outcomeImpactLabel}>User impact</p>
                  <ul className={page.outcomeImpactList}>
                    <li>Reduced cognitive overload during exploration and decision-making</li>
                    <li>Enabled users to find relevant information quickly without prior knowledge</li>
                    <li>
                      Made the experience more approachable for students with varying levels of guidance and
                      support
                    </li>
                  </ul>
                </div>
                <div className={page.outcomeImpactBlock}>
                  <p className={page.outcomeImpactLabel}>Business impact</p>
                  <ul className={page.outcomeImpactList}>
                    <li>Decreased support tickets, indicating clearer product understanding</li>
                    <li>Strengthened confidence among students and guidance counselors</li>
                    <li>Enabled the organization to scale its product suite on a stronger foundation</li>
                  </ul>
                </div>
              </div>
              <div className={page.statsBarOutcomeWrap}>
                <CaseStudyStatsBar
                  animateOnScroll
                  className={page.statsBarOutcomeGrid}
                  donutVariant="cdOutcome"
                  staggerModulo={2}
                  stats={[
                    { value: '74%', label: 'faster intake', progress: 0.74, captionSize: 'sm' },
                    {
                      value: '51%',
                      label: 'shorter filter/search time',
                      progress: 0.51,
                      captionSize: 'sm',
                    },
                    { value: '1', label: 'unified design system', progress: 1, captionSize: 'md' },
                    {
                      value: '21%',
                      label: 'decreased support tickets',
                      progress: 0.21,
                      captionSize: 'md',
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className={page.section} id="section-takeaways">
        <div className={page.inner}>
          <div className={page.takeawaysBlock}>
            <CaseStudySectionHeader
              kicker="TAKEAWAYS"
              title="I Scale the Right Solutions, Not Just Improve the Experience"
              titleClassName={cards.sectionTitleLarge}
              asideImg={takeawaysEmoji}
              asideAlt="Decorative avatar"
              asideImgClassName={cards.sectionAsideImgSm}
            />
            <CaseStudyDoubleCardRow
              className={cards.doubleRowAlignStart}
              left={
                <CaseStudyContentCard
                  label="What I learned"
                  surface="none"
                  labelTone="teal"
                  className={cards.contentCardPaddingNone}
                >
                  <CaseStudyBulletList
                    items={[
                      "The most impactful decisions aren't always visual, they are strategic.",
                      'Clarity is a strategic advantage; in complex domains like education, clarity directly impacts trust, engagement, and decision-making.',
                      'Search cannot replace structure; effective products guide users through structured discovery, not just query-based access.',
                      'Not all data should be quantified. In mission-driven products, over-structuring can erode what makes the experience valuable.',
                      'Design systems are product infrastructure that enable teams to move faster and more consistently.',
                    ]}
                  />
                </CaseStudyContentCard>
              }
              right={
                <CaseStudyContentCard
                  label="What this work demonstrates"
                  surface="none"
                  labelTone="teal"
                  className={cards.contentCardPaddingNone}
                >
                  <CaseStudyBulletList
                    items={[
                      'I prioritize systems over surfaces: navigation, content, and structure before UI polish',
                      'I challenge roadmap assumptions when they conflict with user outcomes',
                      'I balance differentiation with scalability, especially in mission-driven products',
                      'I lead through influence, aligning cross-functional teams on higher-impact decisions',
                      'I design for equity, reducing reliance on prior knowledge or insider guidance',
                    ]}
                  />
                </CaseStudyContentCard>
              }
            />
          </div>
        </div>
      </section>
      <ScrollToTopButton />
    </main>
  );
}
